import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  BackgroundVariant,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ArrowLeft, Save, Plus, Image, FileAudio, Video, FileText, Clock, Send, ShoppingCart, Brain, Building2, ShoppingBag, Calendar, CalendarCheck, Check, Headphones, ClipboardList, UserCog, UserCheck, Sparkles, Flag, Wand2, CheckCircle, LayoutGrid, Bell } from 'lucide-react';
import { Button, Card, Input, useToast, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@caruso/ui';
import { flowsService, CreateFlowDto, UpdateFlowDto } from '@/services/flows.service';
import { listsService } from '@/services/lists.service';
import { aiService } from '@/services/ai.service';
import { venuesService } from '@/services/venues.service';
import { productsService } from '@/services/products.service';
import { boardsService } from '@/services/boards.service';
import { StartNode } from './nodes/StartNode';
import { MessageNode } from './nodes/MessageNode';
import { DelayNode } from './nodes/DelayNode';
import { MediaNode } from './nodes/MediaNode';
import { QuickSaleNode } from './nodes/QuickSaleNode';
import { SmartResponseNode } from './nodes/SmartResponseNode';
import { VenueOfferNode } from './nodes/VenueOfferNode';
import { PostSaleNode } from './nodes/PostSaleNode';
import { PostReservationNode } from './nodes/PostReservationNode';
import { ScheduleActionNode } from './nodes/ScheduleActionNode';
import { SupportMessageNode } from './nodes/SupportMessageNode';
import { ScheduleCheckNode } from './nodes/ScheduleCheckNode';
import { ScheduleCreateNode } from './nodes/ScheduleCreateNode';
import { FormNode } from './nodes/FormNode';
import { UpdateClientNode } from './nodes/UpdateClientNode';
import CheckClientNode from './nodes/CheckClientNode';
import { EndFlowNode } from './nodes/EndFlowNode';
import { VenueBookingWizardNode } from './nodes/VenueBookingWizardNode';
import { VenueConfirmBookingNode } from './nodes/VenueConfirmBookingNode';
import { BoardStepNode } from './nodes/BoardStepNode';
import { NotificationNode } from './nodes/NotificationNode';
import { FlowProvider } from './FlowContext';

const nodeTypes = {
  start: StartNode,
  message: MessageNode,
  delay: DelayNode,
  media: MediaNode,
  'quick-sale': QuickSaleNode,
  'smart_response': SmartResponseNode,
  'venue-offer': VenueOfferNode,
  'post-sale': PostSaleNode,
  'post-reservation': PostReservationNode,
  'schedule-action': ScheduleActionNode,
  'support_message': SupportMessageNode,
  'check-client': CheckClientNode,
  'schedule_check': ScheduleCheckNode,
  'schedule_create': ScheduleCreateNode,
  form: FormNode,
  'update-client': UpdateClientNode,
  'end_flow': EndFlowNode,
  'venue_booking_wizard': VenueBookingWizardNode,
  'venue_confirm_booking': VenueConfirmBookingNode,
  'board_step': BoardStepNode,
  'notification': NotificationNode,
};

const initialNodes: Node[] = [
  {
    id: 'start-1',
    type: 'start',
    position: { x: 250, y: 50 },
    data: { label: 'Início' },
  },
];

function FlowEditorContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { project } = useReactFlow();

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [flowType, setFlowType] = useState<'MANUAL' | 'AUTO_SALE' | 'AUTO_RESERVATION' | 'AUTO_KEYWORD'>('MANUAL');
  const [selectedListId, setSelectedListId] = useState('');
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [selectedInitialStepId, setSelectedInitialStepId] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [allowedDays, setAllowedDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('18:00');
  const [delayBetweenMessages, setDelayBetweenMessages] = useState(5);
  const [delayBetweenClients, setDelayBetweenClients] = useState(30);
  const [testPhone, setTestPhone] = useState('');
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialLoadRef = useRef(true);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Load saved test phone from localStorage
  useEffect(() => {
    const savedPhone = localStorage.getItem('flow-test-phone');
    if (savedPhone) {
      setTestPhone(savedPhone);
    }
  }, []);

  // Load flow data if editing
  const { data: flow } = useQuery({
    queryKey: ['flow', id],
    queryFn: () => flowsService.getById(id!),
    enabled: !!id,
    staleTime: Infinity, // Evita refetch automático que sobrescreve alterações
    refetchOnWindowFocus: false, // Evita refetch ao focar na janela
  });

  const { data: lists } = useQuery({
    queryKey: ['lists'],
    queryFn: () => listsService.getAll({ page: 1, limit: 100, activeOnly: true }),
  });

  const { data: venues } = useQuery({
    queryKey: ['venues'],
    queryFn: () => venuesService.getAll({ page: 1, limit: 100, active: true }),
  });

  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => productsService.getAll({ page: 1, limit: 100 }),
  });

  const { data: boards } = useQuery({
    queryKey: ['boards'],
    queryFn: () => boardsService.getAll({ active: true, includeSteps: true }),
  });

  // Get steps from selected board
  const selectedBoard = boards?.find(b => b.id === selectedBoardId);

  useEffect(() => {
    // Só carrega os dados do flow uma vez (no carregamento inicial)
    if (flow && initialLoadRef.current) {
      setName(flow.name);
      setDescription(flow.description || '');
      setFlowType(flow.flowType || 'MANUAL');
      setSelectedListId(flow.listId || '');
      setSelectedVenueId(flow.venueId || '');
      setSelectedProductId(flow.productId || '');
      setSelectedBoardId(flow.boardId || '');
      setSelectedInitialStepId(flow.initialStepId || '');
      setStartTime(flow.startTime || '09:00');
      setEndTime(flow.endTime || '18:00');
      setDelayBetweenMessages(flow.delayBetweenMessages ?? 5);
      setDelayBetweenClients(flow.delayBetweenClients ?? 30);

      if (flow.allowedDays) {
        try {
          setAllowedDays(JSON.parse(flow.allowedDays));
        } catch (e) {
          setAllowedDays([0, 1, 2, 3, 4, 5, 6]);
        }
      }

      if (flow.keywords) {
        try {
          setKeywords(JSON.parse(flow.keywords));
        } catch (e) {
          setKeywords([]);
        }
      }

      try {
        const flowData = JSON.parse(flow.flowData);
        if (flowData.nodes) setNodes(flowData.nodes);
        if (flowData.edges) setEdges(flowData.edges);
      } catch (e) {
        console.error('Error parsing flow data', e);
      }

      // Mark that initial load is complete
      setTimeout(() => {
        initialLoadRef.current = false;
      }, 100);
    }
  }, [flow, setNodes, setEdges]);

  // Auto-add post-reservation node when flowType is AUTO_RESERVATION
  useEffect(() => {
    if (!id && flowType === 'AUTO_RESERVATION') {
      // Check if post-reservation node already exists
      const hasReservationNode = nodes.some(n => n.type === 'post-reservation');

      if (!hasReservationNode) {
        // Add post-reservation node before start node
        const newReservationNode: Node = {
          id: 'reservation-context-1',
          type: 'post-reservation',
          position: { x: 250, y: 50 },
          data: { label: 'Contexto da Reserva' },
        };

        // Update start node position to be below reservation node
        setNodes((nds) => {
          const updatedNodes = nds.map((node) => {
            if (node.id === 'start-1') {
              return { ...node, position: { x: 250, y: 450 } };
            }
            return node;
          });
          return [newReservationNode, ...updatedNodes];
        });

        // Add edge from reservation to start
        setEdges((eds) => [
          ...eds,
          {
            id: 'e-reservation-start',
            source: 'reservation-context-1',
            target: 'start-1',
            type: 'smoothstep',
          },
        ]);
      }
    }
  }, [flowType, id, nodes, setNodes, setEdges]);

  // Auto-add post-sale node when flowType is AUTO_SALE
  useEffect(() => {
    if (!id && flowType === 'AUTO_SALE') {
      // Check if post-sale node already exists
      const hasSaleNode = nodes.some(n => n.type === 'post-sale');

      if (!hasSaleNode) {
        // Add post-sale node before start node
        const newSaleNode: Node = {
          id: 'sale-context-1',
          type: 'post-sale',
          position: { x: 250, y: 50 },
          data: { label: 'Contexto da Venda' },
        };

        // Update start node position to be below sale node
        setNodes((nds) => {
          const updatedNodes = nds.map((node) => {
            if (node.id === 'start-1') {
              return { ...node, position: { x: 250, y: 450 } };
            }
            return node;
          });
          return [newSaleNode, ...updatedNodes];
        });

        // Add edge from sale to start
        setEdges((eds) => [
          ...eds,
          {
            id: 'e-sale-start',
            source: 'sale-context-1',
            target: 'start-1',
            type: 'smoothstep',
          },
        ]);
      }
    }
  }, [flowType, id, nodes, setNodes, setEdges]);

  const onConnect = useCallback(
    (params: Connection | Edge) => setEdges((eds) => addEdge(params, eds)),
    [setEdges],
  );

  // Handler para deletar nós e edges com tecla Delete/Backspace
  const onNodesDelete = useCallback((deleted: Node[]) => {
    // Remove edges conectadas aos nós deletados
    const deletedIds = deleted.map((node) => node.id);
    setEdges((eds) => eds.filter((edge) =>
      !deletedIds.includes(edge.source) && !deletedIds.includes(edge.target)
    ));
  }, [setEdges]);

  const onEdgesDelete = useCallback((deleted: Edge[]) => {
    // Edges são automaticamente removidas pelo ReactFlow
  }, []);

  const autoSaveMutation = useMutation({
    mutationFn: async (data: CreateFlowDto | UpdateFlowDto) => {
      if (id) {
        return flowsService.update(id, data);
      }
      return flowsService.create(data as CreateFlowDto);
    },
    onSuccess: (flow) => {
      queryClient.invalidateQueries({ queryKey: ['flows'] });
      setAutoSaveStatus('saved');
      // Se for um novo fluxo, redireciona para edição com o ID
      if (!id && flow.id) {
        navigate(`/app/fluxos/${flow.id}/editar`, { replace: true });
      }
      // Reset status após 2 segundos
      setTimeout(() => {
        setAutoSaveStatus('idle');
      }, 2000);
    },
    onError: () => {
      setAutoSaveStatus('idle');
      toast({
        title: 'Erro ao salvar automaticamente',
        description: 'Tente salvar manualmente',
        variant: 'destructive',
      });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: CreateFlowDto | UpdateFlowDto) => {
      if (id) {
        return flowsService.update(id, data);
      }
      return flowsService.create(data as CreateFlowDto);
    },
    onSuccess: (flow) => {
      queryClient.invalidateQueries({ queryKey: ['flows'] });
      toast({
        title: id ? 'Fluxo atualizado!' : 'Fluxo criado!',
        variant: 'success',
      });
      // Se for um novo fluxo, redireciona para edição com o ID
      if (!id && flow.id) {
        navigate(`/app/fluxos/${flow.id}/editar`, { replace: true });
      }
    },
    onError: () => {
      toast({
        title: 'Erro ao salvar fluxo',
        variant: 'destructive',
      });
    },
  });

  const saveAndExitMutation = useMutation({
    mutationFn: async (data: CreateFlowDto | UpdateFlowDto) => {
      if (id) {
        return flowsService.update(id, data);
      }
      return flowsService.create(data as CreateFlowDto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flows'] });
      toast({
        title: id ? 'Fluxo atualizado!' : 'Fluxo criado!',
        variant: 'success',
      });
      navigate('/app/fluxos');
    },
    onError: () => {
      toast({
        title: 'Erro ao salvar fluxo',
        variant: 'destructive',
      });
    },
  });

  const testMutation = useMutation({
    mutationFn: async (phone: string) => {
      if (!id) {
        throw new Error('Fluxo precisa ser salvo antes de testar');
      }
      return flowsService.startFlow(id, { clientPhones: [phone] });
    },
    onSuccess: () => {
      toast({
        title: 'Teste iniciado!',
        description: 'O fluxo está sendo enviado para o número de teste',
        variant: 'success',
      });
      setTestPhone('');
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao iniciar teste',
        description: error.response?.data?.message || error.message || 'Erro desconhecido',
        variant: 'destructive',
      });
    },
  });

  const handleSave = () => {
    if (!name) {
      toast({
        title: 'Nome obrigatório',
        description: 'Preencha o nome do fluxo',
        variant: 'destructive',
      });
      return;
    }

    // Validação: AUTO_RESERVATION deve ter um ambiente selecionado
    if (flowType === 'AUTO_RESERVATION' && !selectedVenueId) {
      toast({
        title: 'Ambiente obrigatório',
        description: 'Selecione um ambiente para fluxos do tipo Pós-Reserva',
        variant: 'destructive',
      });
      return;
    }

    const flowData = JSON.stringify({ nodes, edges });

    const data: CreateFlowDto = {
      name,
      description,
      flowType,
      flowData,
      listId: selectedListId || null,
      venueId: selectedVenueId || undefined,
      productId: selectedProductId || undefined,
      boardId: selectedBoardId || undefined,
      initialStepId: selectedInitialStepId || undefined,
      allowedDays: JSON.stringify(allowedDays),
      startTime,
      endTime,
      delayBetweenMessages,
      delayBetweenClients,
      keywords: keywords.length > 0 ? JSON.stringify(keywords) : undefined,
      active: true,
    };

    saveMutation.mutate(data);
  };

  const handleSaveAndExit = () => {
    if (!name) {
      toast({
        title: 'Nome obrigatório',
        description: 'Preencha o nome do fluxo',
        variant: 'destructive',
      });
      return;
    }

    // Validação: AUTO_RESERVATION deve ter um ambiente selecionado
    if (flowType === 'AUTO_RESERVATION' && !selectedVenueId) {
      toast({
        title: 'Ambiente obrigatório',
        description: 'Selecione um ambiente para fluxos do tipo Pós-Reserva',
        variant: 'destructive',
      });
      return;
    }

    const flowData = JSON.stringify({ nodes, edges });

    const data: CreateFlowDto = {
      name,
      description,
      flowType,
      flowData,
      listId: selectedListId || null,
      venueId: selectedVenueId || undefined,
      productId: selectedProductId || undefined,
      boardId: selectedBoardId || undefined,
      initialStepId: selectedInitialStepId || undefined,
      allowedDays: JSON.stringify(allowedDays),
      startTime,
      endTime,
      delayBetweenMessages,
      delayBetweenClients,
      keywords: keywords.length > 0 ? JSON.stringify(keywords) : undefined,
      active: true,
    };

    saveAndExitMutation.mutate(data);
  };

  const handleTest = () => {
    if (!id) {
      toast({
        title: 'Salve o fluxo primeiro',
        description: 'É necessário salvar o fluxo antes de testar',
        variant: 'destructive',
      });
      return;
    }

    if (!testPhone) {
      toast({
        title: 'Número obrigatório',
        description: 'Informe o número de telefone para teste',
        variant: 'destructive',
      });
      return;
    }

    // Remove caracteres não numéricos
    const cleanPhone = testPhone.replace(/\D/g, '');

    if (cleanPhone.length < 10) {
      toast({
        title: 'Número inválido',
        description: 'Informe um número de telefone válido com DDD',
        variant: 'destructive',
      });
      return;
    }

    // Save phone to localStorage for reuse
    localStorage.setItem('flow-test-phone', testPhone);

    testMutation.mutate(cleanPhone);
  };

  const handleGenerateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast({
        title: 'Descrição obrigatória',
        description: 'Descreva o que você quer no fluxo',
        variant: 'destructive',
      });
      return;
    }

    setIsGenerating(true);

    try {
      const result = await aiService.generateFlow({
        prompt: aiPrompt,
        currentFlow: {
          nodes,
          edges,
        },
        flowDescription: description,
      });

      // Atualizar os nós e edges com o resultado da IA
      setNodes(result.nodes);
      setEdges(result.edges);

      toast({
        title: 'Fluxo gerado com sucesso!',
        description: 'O fluxo foi criado pela IA. Revise e ajuste conforme necessário.',
        variant: 'success',
      });

      setAiDialogOpen(false);
      setAiPrompt('');
    } catch (error: any) {
      console.error('Error generating flow:', error);
      toast({
        title: 'Erro ao gerar fluxo',
        description: error.response?.data?.message || error.message || 'Erro desconhecido',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Auto-save effect - triggers when nodes or edges change
  useEffect(() => {
    // Skip auto-save on initial load or if name is empty
    if (initialLoadRef.current || !name || !id) {
      return;
    }

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set status to saving
    setAutoSaveStatus('saving');

    // Debounce auto-save by 2 seconds
    autoSaveTimeoutRef.current = setTimeout(() => {
      const flowData = JSON.stringify({ nodes, edges });

      const data: UpdateFlowDto = {
        name,
        description,
        flowType,
        flowData,
        listId: selectedListId || null,
        boardId: selectedBoardId || undefined,
        initialStepId: selectedInitialStepId || undefined,
        allowedDays: JSON.stringify(allowedDays),
        startTime,
        endTime,
        delayBetweenMessages,
        delayBetweenClients,
        keywords: keywords.length > 0 ? JSON.stringify(keywords) : undefined,
        active: true,
      };

      autoSaveMutation.mutate(data);
    }, 2000);

    // Cleanup on unmount
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [nodes, edges, name, description, flowType, selectedListId, selectedBoardId, selectedInitialStepId, keywords, allowedDays, startTime, endTime, delayBetweenMessages, delayBetweenClients, id]);

  const addNode = (type: string, mediaType?: string) => {
    // Calcular posição no centro da tela visível
    const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
    let position = { x: 250, y: 250 }; // Posição padrão caso não consiga calcular

    if (reactFlowBounds) {
      // Pegar o centro da área visível
      const centerX = reactFlowBounds.width / 2;
      const centerY = reactFlowBounds.height / 2;

      // Converter coordenadas da tela para coordenadas do fluxo
      position = project({
        x: centerX,
        y: centerY
      });
    }

    const newNode: Node = {
      id: `${type}-${Date.now()}`,
      type: type === 'image' || type === 'audio' || type === 'video' || type === 'document' ? 'media' : type,
      position,
      data: {
        label: type === 'message' ? 'Nova Mensagem' :
               type === 'delay' ? 'Aguardar' :
               type === 'quick-sale' ? 'Venda Rápida' :
               type === 'smart_response' ? 'Resposta Inteligente' :
               type === 'post-sale' ? 'Pós-Venda' :
               type === 'post-reservation' ? 'Pós-Reserva' :
               type === 'schedule-action' ? 'Agendar Ação' :
               type === 'support_message' ? 'Mensagem de Apoio' :
               type === 'schedule_check' ? 'Consulta de Agendamento' :
               type === 'schedule_create' ? 'Criar Agendamento' :
               type === 'form' ? 'Formulário' :
               type === 'update-client' ? 'Atualizar Cliente' :
               type === 'end_flow' ? 'Finalizar Fluxo' :
               type === 'venue_booking_wizard' ? 'Wizard de Reserva' :
               type === 'venue_confirm_booking' ? 'Confirmar Reserva' :
               type === 'board_step' ? 'Mover no Quadro' :
               type === 'notification' ? 'Enviar Notificacao' :
               type === 'image' ? 'Imagem' :
               type === 'audio' ? 'Áudio' :
               type === 'video' ? 'Vídeo' :
               type === 'document' ? 'Documento' : 'Mídia',
        message: type === 'message' ? '' : undefined,
        delay: type === 'delay' ? 5 : undefined,
        mediaType: mediaType || type,
        smartMessage: type === 'smart_response' ? '' : undefined,
        responseOptions: type === 'smart_response' ? [] : undefined,
        useAI: type === 'smart_response' ? false : undefined,
        delayMinutes: type === 'schedule-action' ? 60 : undefined,
        actionType: type === 'schedule-action' ? 'message' : undefined,
        flowId: type === 'schedule-action' ? '' : undefined,
        // Passa o boardId do fluxo para o nó board_step
        boardId: type === 'board_step' ? selectedBoardId : undefined,
        boardName: type === 'board_step' ? selectedBoard?.name : undefined,
      },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const toggleDay = (day: number) => {
    setAllowedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()
    );
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-brand-ink border-b border-brand-gold/20 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/app/fluxos')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {id ? 'Editar Fluxo' : 'Novo Fluxo'}
              </h1>
              {id && (
                <div className="flex items-center gap-2 mt-1">
                  {autoSaveStatus === 'saving' && (
                    <span className="text-xs text-brand-ivory/60 flex items-center gap-1">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                      Salvando automaticamente...
                    </span>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <span className="text-xs text-green-500 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Salvo automaticamente
                    </span>
                  )}
                  {autoSaveStatus === 'idle' && (
                    <span className="text-xs text-brand-ivory/40">
                      Salvamento automático ativado
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setAiDialogOpen(true)}
              variant="outline"
              className="gap-2 border-purple-500/40 text-purple-400 hover:bg-purple-500/10"
            >
              <Sparkles className="w-4 h-4" />
              Gerar com IA
            </Button>
            <Button
              onClick={handleSave}
              disabled={saveMutation.isPending}
              variant="outline"
            >
              <Save className="w-4 h-4 mr-2" />
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button
              onClick={handleSaveAndExit}
              disabled={saveAndExitMutation.isPending}
            >
              <Save className="w-4 h-4 mr-2" />
              {saveAndExitMutation.isPending ? 'Salvando...' : 'Salvar e Sair'}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="w-80 bg-brand-ink border-r border-brand-gold/20 p-4 overflow-y-auto">
          <div className="space-y-6">
            <div>
              <h3 className="text-white font-semibold mb-3 text-lg">Configurações</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">Nome do Fluxo *</label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ex: Boas-vindas"
                    className="bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">Descrição</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descreva o propósito do fluxo..."
                    className="w-full p-3 bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">Tipo de Fluxo *</label>
                  <select
                    value={flowType}
                    onChange={(e) => setFlowType(e.target.value as 'MANUAL' | 'AUTO_SALE' | 'AUTO_RESERVATION' | 'AUTO_KEYWORD')}
                    disabled={!!id}
                    className="w-full p-3 bg-neutral-800/50 border border-neutral-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="MANUAL" className="bg-neutral-900">Manual - Dispara para lista específica</option>
                    <option value="AUTO_SALE" className="bg-neutral-900">Automático - Pós-venda</option>
                    <option value="AUTO_RESERVATION" className="bg-neutral-900">Automático - Pós-reserva</option>
                    <option value="AUTO_KEYWORD" className="bg-neutral-900">Automático - Por palavra-chave</option>
                  </select>
                  <p className="text-xs text-brand-ivory/60 mt-1">
                    {flowType === 'MANUAL' && 'Fluxo disparado manualmente para uma lista de contatos'}
                    {flowType === 'AUTO_SALE' && 'Disparado automaticamente quando uma venda é confirmada'}
                    {flowType === 'AUTO_RESERVATION' && 'Disparado automaticamente quando uma reserva é confirmada'}
                    {flowType === 'AUTO_KEYWORD' && 'Disparado automaticamente quando cliente envia uma palavra-chave específica'}
                    {!!id && ' • O tipo de fluxo não pode ser alterado após a criação'}
                  </p>
                </div>

                {flowType === 'AUTO_RESERVATION' && (
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory mb-2">Ambiente * {!!id && '(não editável)'}</label>
                    <select
                      value={selectedVenueId}
                      onChange={(e) => setSelectedVenueId(e.target.value)}
                      disabled={!!id}
                      className="w-full p-3 bg-neutral-800/50 border border-neutral-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">Selecione um ambiente</option>
                      {venues?.data.map((venue: any) => (
                        <option key={venue.id} value={venue.id} className="bg-neutral-900">
                          {venue.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-brand-ivory/60 mt-1">
                      Selecione o ambiente para o qual este fluxo será disparado após uma reserva confirmada.
                      {!!id && ' Após a criação, o ambiente não pode ser alterado.'}
                    </p>
                  </div>
                )}

                {flowType === 'AUTO_SALE' && (
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory mb-2">Produto {!!id && '(não editável)'}</label>
                    <select
                      value={selectedProductId}
                      onChange={(e) => setSelectedProductId(e.target.value)}
                      disabled={!!id}
                      className="w-full p-3 bg-neutral-800/50 border border-neutral-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">Genérico (todos os produtos)</option>
                      {products?.data.map((product: any) => (
                        <option key={product.id} value={product.id} className="bg-neutral-900">
                          {product.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-brand-ivory/60 mt-1">
                      Opcional: Selecione um produto específico para este fluxo ou deixe como "Genérico" para disparar após qualquer venda.
                      {!!id && ' Após a criação, o produto não pode ser alterado.'}
                    </p>
                  </div>
                )}

                {flowType === 'AUTO_KEYWORD' && (
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory mb-2">Palavras-chave</label>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={keywordInput}
                          onChange={(e) => setKeywordInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && keywordInput.trim()) {
                              e.preventDefault();
                              const keyword = keywordInput.trim().toLowerCase();
                              if (!keywords.includes(keyword)) {
                                setKeywords([...keywords, keyword]);
                                setKeywordInput('');
                              }
                            }
                          }}
                          placeholder="Digite uma palavra-chave e pressione Enter"
                          className="bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
                        />
                        <Button
                          type="button"
                          onClick={() => {
                            const keyword = keywordInput.trim().toLowerCase();
                            if (keyword && !keywords.includes(keyword)) {
                              setKeywords([...keywords, keyword]);
                              setKeywordInput('');
                            }
                          }}
                          variant="outline"
                          disabled={!keywordInput.trim()}
                        >
                          Adicionar
                        </Button>
                      </div>
                      {keywords.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {keywords.map((keyword, index) => (
                            <div
                              key={index}
                              className="bg-brand-gold/20 text-brand-gold px-3 py-1 rounded-full text-sm flex items-center gap-2"
                            >
                              <span>{keyword}</span>
                              <button
                                type="button"
                                onClick={() => setKeywords(keywords.filter((_, i) => i !== index))}
                                className="hover:text-brand-gold/80"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-brand-ivory/50">
                        Exemplos: orçamento, preço, cardápio, reserva
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">Lista de Contatos</label>
                  <select
                    value={selectedListId}
                    onChange={(e) => setSelectedListId(e.target.value)}
                    className="w-full p-3 bg-neutral-800/50 border border-neutral-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                  >
                    <option value="" className="bg-neutral-900">Sem lista padrão</option>
                    {lists?.data.map((list) => (
                      <option key={list.id} value={list.id} className="bg-neutral-900">
                        {list.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">Quadro Kanban</label>
                  <select
                    value={selectedBoardId}
                    onChange={(e) => {
                      setSelectedBoardId(e.target.value);
                      setSelectedInitialStepId('');
                    }}
                    className="w-full p-3 bg-neutral-800/50 border border-neutral-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                  >
                    <option value="" className="bg-neutral-900">Sem quadro</option>
                    {boards?.map((board) => (
                      <option key={board.id} value={board.id} className="bg-neutral-900">
                        {board.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-brand-ivory/50 mt-1">
                    Associe este fluxo a um quadro para acompanhar os clientes em execução
                  </p>
                </div>

                {selectedBoardId && selectedBoard?.steps && selectedBoard.steps.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory mb-2">Passo Inicial no Quadro</label>
                    <select
                      value={selectedInitialStepId}
                      onChange={(e) => setSelectedInitialStepId(e.target.value)}
                      className="w-full p-3 bg-neutral-800/50 border border-neutral-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                    >
                      <option value="" className="bg-neutral-900">Selecione o passo inicial</option>
                      {selectedBoard.steps.map((step) => (
                        <option key={step.id} value={step.id} className="bg-neutral-900">
                          {step.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-brand-ivory/50 mt-1">
                      Os clientes serão adicionados neste passo quando o fluxo iniciar
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-3">Horários</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-brand-ivory/70 mb-1">Início</label>
                    <Input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="bg-neutral-800/50 border-neutral-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-brand-ivory/70 mb-1">Fim</label>
                    <Input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="bg-neutral-800/50 border-neutral-700 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-brand-ivory/70 mb-2">Dias Permitidos</label>
                  <div className="grid grid-cols-7 gap-1">
                    {dayNames.map((day, index) => (
                      <button
                        key={index}
                        onClick={() => toggleDay(index)}
                        className={`p-2 text-xs rounded transition-colors ${
                          allowedDays.includes(index)
                            ? 'bg-brand-gold text-neutral-900'
                            : 'bg-neutral-800 text-brand-ivory/70 hover:bg-neutral-700'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-3">Intervalos</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-brand-ivory/70 mb-1">
                    Entre mensagens ({delayBetweenMessages}s)
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="60"
                    value={delayBetweenMessages}
                    onChange={(e) => setDelayBetweenMessages(parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs text-brand-ivory/70 mb-1">
                    Entre clientes ({delayBetweenClients}s)
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="300"
                    value={delayBetweenClients}
                    onChange={(e) => setDelayBetweenClients(parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-brand-gold/20 pt-6">
              <h3 className="text-white font-semibold mb-3">Testar Fluxo</h3>
              <div className="space-y-3">
                <div className="bg-neutral-800/50 border border-brand-gold/20 rounded-lg p-3">
                  <p className="text-xs text-brand-ivory/70 mb-3">
                    Envie o fluxo para um número de teste antes de ativar para todos os clientes.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-brand-ivory/70 mb-1">
                        Número de Telefone (com DDD)
                      </label>
                      <Input
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                        placeholder="11987654321"
                        className="bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
                        disabled={testMutation.isPending}
                      />
                    </div>
                    <Button
                      onClick={handleTest}
                      disabled={!id || testMutation.isPending || !testPhone}
                      className="w-full bg-brand-gold hover:bg-brand-gold/90 text-brand-ink font-semibold"
                      size="sm"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {testMutation.isPending ? 'Enviando...' : 'Enviar Teste'}
                    </Button>
                    {!id && (
                      <p className="text-xs text-yellow-500">
                        Salve o fluxo primeiro para poder testar
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-3">Adicionar Nó</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-brand-ivory/60 mb-2 uppercase tracking-wide">Mensagens</p>
                  <div className="space-y-2">
                    <Button onClick={() => addNode('message')} variant="outline" className="w-full justify-start">
                      <Plus className="w-4 h-4 mr-2" />
                      Texto
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-brand-ivory/60 mb-2 uppercase tracking-wide">Mídia</p>
                  <div className="space-y-2">
                    <Button onClick={() => addNode('image')} variant="outline" className="w-full justify-start">
                      <Image className="w-4 h-4 mr-2" />
                      Imagem
                    </Button>
                    <Button onClick={() => addNode('audio')} variant="outline" className="w-full justify-start">
                      <FileAudio className="w-4 h-4 mr-2" />
                      Áudio
                    </Button>
                    <Button onClick={() => addNode('video')} variant="outline" className="w-full justify-start">
                      <Video className="w-4 h-4 mr-2" />
                      Vídeo
                    </Button>
                    <Button onClick={() => addNode('document')} variant="outline" className="w-full justify-start">
                      <FileText className="w-4 h-4 mr-2" />
                      Documento
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-brand-ivory/60 mb-2 uppercase tracking-wide">Ações</p>
                  <div className="space-y-2">
                    <Button onClick={() => addNode('quick-sale')} variant="outline" className="w-full justify-start">
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Venda Rápida
                    </Button>
                    <Button onClick={() => addNode('venue-offer')} variant="outline" className="w-full justify-start">
                      <Building2 className="w-4 h-4 mr-2" />
                      Oferta de Ambiente
                    </Button>
                    <Button onClick={() => addNode('support_message')} variant="outline" className="w-full justify-start border-orange-500/40">
                      <Headphones className="w-4 h-4 mr-2" />
                      Mensagem de Apoio
                    </Button>
                  </div>
                </div>

                {/* Flow Type Specific Nodes */}
                {flowType === 'AUTO_SALE' && (
                  <div>
                    <p className="text-xs text-brand-ivory/60 mb-2 uppercase tracking-wide">Automação Pós-Venda</p>
                    <div className="space-y-2">
                      <Button onClick={() => addNode('post-sale')} variant="outline" className="w-full justify-start border-green-500/40">
                        <ShoppingBag className="w-4 h-4 mr-2" />
                        Pós-Venda
                      </Button>
                    </div>
                  </div>
                )}

                {flowType === 'AUTO_RESERVATION' && (
                  <div>
                    <p className="text-xs text-brand-ivory/60 mb-2 uppercase tracking-wide">Automação Pós-Reserva</p>
                    <div className="space-y-2">
                      <Button onClick={() => addNode('post-reservation')} variant="outline" className="w-full justify-start border-purple-500/40">
                        <Calendar className="w-4 h-4 mr-2" />
                        Pós-Reserva
                      </Button>
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-xs text-brand-ivory/60 mb-2 uppercase tracking-wide">Reserva de Ambiente</p>
                  <div className="space-y-2">
                    <Button onClick={() => addNode('venue_booking_wizard')} variant="outline" className="w-full justify-start border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10">
                      <Wand2 className="w-4 h-4 mr-2 text-emerald-400" />
                      <span className="text-emerald-400">Wizard de Reserva</span>
                    </Button>
                    <Button onClick={() => addNode('venue_confirm_booking')} variant="outline" className="w-full justify-start border-green-500/40">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Confirmar Reserva
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-brand-ivory/60 mb-2 uppercase tracking-wide">Interações</p>
                  <div className="space-y-2">
                    <Button onClick={() => addNode('smart_response')} variant="outline" className="w-full justify-start">
                      <Brain className="w-4 h-4 mr-2" />
                      Resposta Inteligente
                    </Button>
                    <Button onClick={() => addNode('form')} variant="outline" className="w-full justify-start border-blue-500/40">
                      <ClipboardList className="w-4 h-4 mr-2" />
                      Formulário
                    </Button>
                    <Button onClick={() => addNode('update-client')} variant="outline" className="w-full justify-start border-purple-500/40">
                      <UserCog className="w-4 h-4 mr-2" />
                      Atualizar Cliente
                    </Button>
                    <Button onClick={() => addNode('check-client')} variant="outline" className="w-full justify-start border-blue-500/40">
                      <UserCheck className="w-4 h-4 mr-2" />
                      Verificar Cliente
                    </Button>
                    <Button onClick={() => addNode('schedule_check')} variant="outline" className="w-full justify-start border-purple-500/40">
                      <Calendar className="w-4 h-4 mr-2" />
                      Consulta de Agendamento
                    </Button>
                    <Button onClick={() => addNode('schedule_create')} variant="outline" className="w-full justify-start border-green-500/40">
                      <CalendarCheck className="w-4 h-4 mr-2" />
                      Criar Agendamento
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-brand-ivory/60 mb-2 uppercase tracking-wide">Controle</p>
                  <div className="space-y-2">
                    <Button onClick={() => addNode('delay')} variant="outline" className="w-full justify-start">
                      <Clock className="w-4 h-4 mr-2" />
                      Aguardar
                    </Button>
                    <Button onClick={() => addNode('schedule-action')} variant="outline" className="w-full justify-start border-purple-500/40">
                      <Clock className="w-4 h-4 mr-2" />
                      Agendar Ação
                    </Button>
                    <Button onClick={() => addNode('board_step')} variant="outline" className="w-full justify-start border-indigo-500/40">
                      <LayoutGrid className="w-4 h-4 mr-2" />
                      Mover no Quadro
                    </Button>
                    <Button onClick={() => addNode('notification')} variant="outline" className="w-full justify-start border-amber-500/40">
                      <Bell className="w-4 h-4 mr-2" />
                      Enviar Notificacao
                    </Button>
                    <Button onClick={() => addNode('end_flow')} variant="outline" className="w-full justify-start border-green-500/40">
                      <Flag className="w-4 h-4 mr-2" />
                      Finalizar Fluxo
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Flow Canvas */}
        <div className="flex-1 bg-brand-ink" ref={reactFlowWrapper}>
          <FlowProvider boardId={selectedBoardId || null} boardName={selectedBoard?.name || null}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodesDelete={onNodesDelete}
              onEdgesDelete={onEdgesDelete}
              nodeTypes={nodeTypes}
              deleteKeyCode={['Backspace', 'Delete']}
              edgesReconnectable={true}
              reconnectRadius={20}
              fitView
            >
              <Controls />
              <MiniMap />
              <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
            </ReactFlow>
          </FlowProvider>
        </div>
      </div>

      {/* AI Generation Dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="bg-brand-ink border-brand-gold/20">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              Gerar Fluxo com IA
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/70">
              Descreva o que você quer adicionar ou modificar no fluxo e a IA irá gerar automaticamente os nós necessários.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-brand-ivory mb-2">
                O que você quer no fluxo?
              </label>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Ex: Adicione uma mensagem de boas-vindas, depois pergunte o nome do cliente e aguarde 5 segundos antes de enviar uma oferta de produtos"
                className="w-full p-3 bg-neutral-800/50 border border-neutral-700 text-white placeholder:text-neutral-500 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50 min-h-[120px]"
                disabled={isGenerating}
              />
              <p className="text-xs text-brand-ivory/50 mt-2">
                💡 Dica: Seja específico sobre a ordem das ações, tempos de espera e tipos de mensagens que deseja.
              </p>
            </div>

            {nodes.length > 1 && (
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                <p className="text-xs text-purple-300">
                  <strong>Fluxo atual detectado:</strong> A IA irá considerar os nós existentes e adicionar/modificar conforme sua solicitação.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setAiDialogOpen(false);
                setAiPrompt('');
              }}
              disabled={isGenerating}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleGenerateWithAI}
              disabled={isGenerating || !aiPrompt.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2" />
                  Gerando...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Gerar Fluxo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function FlowEditor() {
  return (
    <ReactFlowProvider>
      <FlowEditorContent />
    </ReactFlowProvider>
  );
}
